import { useState, useEffect, useRef } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";
import type { DatePreset, DateWindow } from "../utils/dateWindow";
import { presetToWindow, windowToPreset } from "../utils/dateWindow";

export type { DatePreset } from "../utils/dateWindow";

export interface UseDateRangeFilterProps {
  dateWindow?: DateWindow;
  onDateWindowChange?: (window?: DateWindow) => void;
}

/**
 * State and interactions for the date-range picker.
 *
 * The selected preset is now *read from the window* rather than inferred from
 * the dates it resolves to. The old `getPresetFromFilter` recomputed every
 * preset against today and string-matched, so the moment the date rolled over
 * nothing matched and the dropdown silently fell back to "custom" — see
 * utils/dateWindow.
 */
export function useDateRangeFilter({
  dateWindow,
  onDateWindowChange,
}: UseDateRangeFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(() =>
    windowToPreset(dateWindow),
  );
  const absolute = dateWindow?.kind === "absolute" ? dateWindow : undefined;
  const [customStartDate, setCustomStartDate] = useState<string>(
    absolute?.start || "",
  );
  const [customEndDate, setCustomEndDate] = useState<string>(
    absolute?.end || "",
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the window when it changes underneath us (a saved view, a drill-down).
  const syncToWindow = () => {
    const preset = windowToPreset(dateWindow);
    setSelectedPreset(preset);
    if (preset === "custom") {
      setCustomStartDate(absolute?.start || "");
      setCustomEndDate(absolute?.end || "");
    }
  };

  useEffect(syncToWindow, [dateWindow]);

  useClickOutside(
    containerRef,
    () => {
      if (isExpanded) {
        setIsExpanded(false);
        syncToWindow();
      }
    },
    isExpanded,
  );

  const handlePresetSelect = (preset: DatePreset) => {
    setSelectedPreset(preset);
    if (preset !== "custom") {
      // Auto-apply preset selections.
      onDateWindowChange?.(presetToWindow(preset));
      setIsExpanded(false);
    }
  };

  const handleApply = () => {
    if (selectedPreset === "custom") {
      if (customStartDate && customEndDate && customStartDate > customEndDate) {
        // Invalid range — don't apply.
        return;
      }
      if (customStartDate || customEndDate) {
        onDateWindowChange?.({
          kind: "absolute",
          start: customStartDate || undefined,
          end: customEndDate || undefined,
        });
      } else {
        // Both empty = all time.
        onDateWindowChange?.({ kind: "all" });
      }
    } else {
      onDateWindowChange?.(presetToWindow(selectedPreset));
    }
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setIsExpanded(false);
    syncToWindow();
  };

  const handleRemove = () => {
    onDateWindowChange?.({ kind: "all" });
    setSelectedPreset("allTime");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const canApply = (): boolean => {
    if (selectedPreset === "custom") {
      if (customStartDate && customEndDate) {
        return customStartDate <= customEndDate;
      }
      return !!(customStartDate || customEndDate);
    }
    return true; // Presets can always be applied.
  };

  return {
    isExpanded,
    setIsExpanded,
    selectedPreset,
    setSelectedPreset,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    containerRef,
    handlePresetSelect,
    handleApply,
    handleCancel,
    handleRemove,
    isActive: !!dateWindow && dateWindow.kind !== "all",
    canApply: canApply(),
  };
}
